/**
 * API utilities for form submissions (SvelteKit server endpoints)
 */
import { getUtmParams } from '../utils/utm';
import { z } from 'zod';

export interface ApiResponse {
    success: boolean;
    message?: string;
    errors?: Record<string, string>;
}

// Zod schema for API response validation
const apiResponseSchema = z.object({
	success: z.boolean(),
	message: z.string().optional(),
	errors: z.record(z.string(), z.string()).optional()
});


/**
 * Sanitize UTM parameters to prevent injection attacks
 */
function sanitizeUtmParams(params: Record<string, string | undefined>): Record<string, string> {
    const sanitized: Record<string, string> = {};

    for (const [key, value] of Object.entries(params)) {
        // Skip undefined values
        if (value === undefined) continue;

        // Sanitize key: remove HTML/JS characters
        const cleanKey = key
            .replace(/[<>"']/g, '')
            .replace(/javascript:/gi, '')
            .substring(0, 50);

        // Sanitize value: remove HTML/JS characters and limit length
        const cleanValue = value
            .replace(/[<>]/g, '')
            .replace(/javascript:/gi, '')
            .replace(/on\w+\s*=/gi, '')
            .substring(0, 100);

        if (cleanKey && cleanValue) {
            sanitized[cleanKey] = cleanValue;
        }
    }

    return sanitized;
}

/**
 * More permissive email validation (follows HTML5 spec mostly)
 */
function isValidEmail(email: string): boolean {
    if (!email || email.length > 254) return false;

    // Basic structure check - more permissive for international addresses
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;

    // Additional checks for obviously invalid patterns
    const hasInvalidChars = /[()<>,;:"[\]]/.test(email);
    if (hasInvalidChars) return false;

    return true;
}
const REQUEST_TIMEOUT = 10000; // 10 seconds

/**
 * Helper to make API requests with timeout and error handling
 * Note: Now uses internal SvelteKit API routes instead of external Express server
 */
async function apiRequest(endpoint: string, data: Record<string, unknown>): Promise<ApiResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
        // Inject UTM parameters implicitly on every request if present
        const utmParams = getUtmParams();
        if (Object.keys(utmParams).length > 0) {
            data.metadata = { ...(data.metadata as Record<string, unknown> || {}), ...sanitizeUtmParams(utmParams as Record<string, string | undefined>) };
        }

        // Add source to metadata if provided
        if (data.source && typeof data.source === 'string') {
            data.metadata = { ...(data.metadata as Record<string, unknown> || {}), source: data.source.replace(/[<>"']/g, "").replace(/javascript:/gi, "").substring(0, 50) };
            delete data.source; // Remove from top level
        }

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            return { success: false, message: `Server error: ${response.status}` };
        }

        const rawData = await response.json();
        const result = apiResponseSchema.safeParse(rawData);

        if (!result.success) {
            console.error('Invalid API response:', rawData);
            return { success: false, message: 'Invalid server response' };
        }

        return result.data;
    } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof Error && error.name === 'AbortError') {
            return { success: false, message: 'Request timed out. Please try again.' };
        }

        if (error instanceof TypeError && error.message.includes('fetch')) {
            return { success: false, message: 'Network error. Please check your connection.' };
        }

        return { success: false, message: 'An unexpected error occurred. Please try again.' };
    }
}

/**
 * Submit newsletter subscription
 */
export async function submitNewsletter(data: {
    email: string;
    honeypot?: string;
    turnstileToken?: string;
    source?: string;
} & Record<string, unknown>): Promise<ApiResponse> {
    return apiRequest('/api/forms/newsletter', data);
}

/**
 * Submit contact form
 */
export async function submitContact(data: {
    name: string;
    email: string;
    inquiryType: string;
    message: string;
    honeypot?: string;
    turnstileToken: string
}): Promise<ApiResponse> {
    return apiRequest('/api/forms/contact', data);
}

 /**
 * Submit enrollment interest form
 */
export async function submitEnrollment(data: {
    name: string;
    email: string;
    phone?: string;
    courses: string[];
    experienceLevel: string;
    message?: string;
    honeypot?: string;
    turnstileToken: string;
    metadata?: Record<string, string | undefined>
}): Promise<ApiResponse> {
    return apiRequest('/api/forms/enrollment', data);
}

 /**
 * Submit consultation request form
 */
export async function submitConsultation(data: {
    name: string;
    email: string;
    projectType: string;
    description: string;
    timeline?: string;
    budget?: string;
    honeypot?: string;
    turnstileToken: string
}): Promise<ApiResponse> {
    return apiRequest('/api/forms/consultation', data);
}

 /**
 * Submit masterclass registration
 */
export async function submitMasterclass(data: {
    name: string;
    email: string;
    phone: string;
    audienceType: string;
    appIdea?: string;
    honeypot?: string;
    turnstileToken: string;
    metadata?: Record<string, string | undefined>;
    pricingDisplay?: string;
}): Promise<ApiResponse> {
    return apiRequest('/api/forms/masterclass', data);
}

/**
 * Submit AI Lab email capture (hero inline or gate)
 */
export async function submitPlaygroundEmail(data: {
    email: string;
    source?: 'hero' | 'gate';
    honeypot?: string;
    turnstileToken: string;
    metadata?: Record<string, string | undefined>;
}): Promise<ApiResponse> {
    return apiRequest('/api/forms/playground-email', data);
}
